import { Viewer } from './Viewer';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { SAOPass } from 'three/examples/jsm/postprocessing/SAOPass';
import { RenderPixelatedPass } from 'three/examples/jsm/postprocessing/RenderPixelatedPass';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass'
import { SSAARenderPass } from 'three/examples/jsm/postprocessing/SSAARenderPass'
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass'
import { WebGLCapabilities } from 'three';

import * as THREE from 'three';
import { Bitflag } from './Helpers/Bitflag';

export enum PostEffects {
    NONE = 0,
    SAOS = 1 << 1,
    PIXEL = 1 << 2,
    GLITCH = 1 << 3,
    OUTLINE = 1 << 4,
    ALL = ~NONE
}

export enum AntiAliasing {
    NONE = 0,
    FXAA = 1,
    SSAA = 2,
    SMAA = 3,
    MSAA = 4
}


export class PostProcessingManager {


    /**
     * Composer for all AA and post effects
     */
    private _effectComposer!: EffectComposer;

    //---AA---
    /**
     * Fast approximate AA
     */
    private _fxaaPass: ShaderPass | null = null;

    /**
     *Super sampling AA
     */
    private _ssaaPass: SSAARenderPass | null = null;

    /**
     * Subpixel Morphological AA
     */
    private _smaaPass: SMAAPass | null = null;



    /**
     * Flag that defines which AA to use
     */
    private _currentAntiAliasing: AntiAliasing;
    public set AntiAliasing(aa: AntiAliasing) {
        if (this._currentAntiAliasing === aa)
            return;
        let newComposer = (this._currentAntiAliasing === AntiAliasing.MSAA || aa === AntiAliasing.MSAA)
        this._currentAntiAliasing = aa;
        if (newComposer)
            this.createComposer();
        this.ComposeEffects();
    }
    public get AntiAliasing() { return this._currentAntiAliasing; }

    //--- Post Effects ---
    /**
     * Scalable Ambient Occlusion
     */
    private _saoPass: SAOPass | null = null;
    /**
     * Pixel
     */
    private _pixelPass: RenderPixelatedPass | null = null;
    /**
     * Glitch
     */
    private _glitchPass: ShaderPass | null = null;
    /**
     * Outline
     */
    private _outlinePass: OutlinePass | null = null;
    /**
     * Render
     */
    private _renderPass: RenderPass | null = null;

    /**
     * Copy
     */
    private _copyPass: ShaderPass | null = null;

    /**
     * Flag that defines which post process effects to use
     */
    private _currentPostEffects: Bitflag<PostEffects> = new Bitflag();

    private _renderTarget: THREE.WebGLRenderTarget | null = null;

    constructor(private viewer: Viewer, dimensions: { width: number, height: number }) {
        this._currentPostEffects.clear();
        this._currentAntiAliasing = AntiAliasing.MSAA.valueOf();
        this._currentPostEffects.addFlag(PostEffects.SAOS);
        // FXAA
        this._fxaaPass = new ShaderPass(FXAAShader);
        

        const pixelRatio = this.viewer._renderer.getPixelRatio();
        this.createComposer();
        this._effectComposer!.setPixelRatio(pixelRatio);
        this.ComposeEffects();
    }

    createComposer() {
        if (this._currentAntiAliasing === AntiAliasing.MSAA && this.viewer._renderer.capabilities.isWebGL2) {
            const size = this.viewer._renderer.getDrawingBufferSize(new THREE.Vector2());
            this._renderTarget = new THREE.WebGLRenderTarget(size.width, size.height, { format: THREE.RGBAFormat, samples: 2 });
            this._effectComposer = new EffectComposer(this.viewer._renderer, this._renderTarget);
        }
        else
            this._effectComposer = new EffectComposer(this.viewer._renderer);
    }



    EnableEffect(effect: PostEffects) {
        this._currentPostEffects.addFlag(effect);
        this.ComposeEffects();
    }

    DisableEffect(effect: PostEffects) {
        this._currentPostEffects.removeFlag(effect);
        this.ComposeEffects();
    }

    DisableAllEffects() {
        this._currentPostEffects.clear();
        this.ComposeEffects();
    }

    EnableAllEffects() {
        this._currentPostEffects.enableAll();
        this.ComposeEffects();
    }

    Render(): boolean {
        if (this._currentPostEffects.allDisabled()) {
            return false;
        }
        // Render
        try {
            this._effectComposer.render();
        }
        catch (error) {
            console.error(error);
            return false;
        }

        return true;
    }

    ComposeEffects() {
        if (!this.viewer.currentScene || !this.viewer.currentCamera)
            throw new Error('Could not compose effect without scene or without camera!')

        this._effectComposer.passes = [];
        let width = this.viewer._width;
        let height = this.viewer._height;
        var clearColor = this.viewer._renderer.getClearColor(new THREE.Color());
        var clearAlpha = this.viewer._renderer.getClearAlpha();
        const pixelRatio = this.viewer._renderer.getPixelRatio();
        this._effectComposer.setPixelRatio(pixelRatio);


        // Default render
        this._renderPass = new RenderPass(this.viewer.currentScene, this.viewer.currentCamera);
        if (width && height)
            this._renderPass.setSize(width, height);
        this._effectComposer.addPass(this._renderPass);


        // ----- ANTI ALIAS -----
        //SSAA
        if (this._currentAntiAliasing === AntiAliasing.SSAA) {
            this._ssaaPass = new SSAARenderPass(this.viewer.currentScene, this.viewer.currentCamera, clearColor, clearAlpha);
            this._ssaaPass.sampleLevel = 2;
            this._effectComposer.addPass(this._ssaaPass);
        }
        // FXAA
        else if (this._currentAntiAliasing === AntiAliasing.FXAA && this._fxaaPass) {
            this._fxaaPass.material.uniforms['resolution'].value.x = 1 / (width * pixelRatio * 2);
            this._fxaaPass.material.uniforms['resolution'].value.y = 1 / (height * pixelRatio * 2);
            this._effectComposer.addPass(this._fxaaPass);
        }
        // SMAA
        else if (this._currentAntiAliasing === AntiAliasing.SMAA) {
            this._smaaPass = new SMAAPass(width * pixelRatio, height * pixelRatio);
            this._effectComposer.addPass(this._smaaPass);
        }

        // ----- POST EFFECTS ----

        //Outline
        if (this._currentPostEffects.hasFlag(PostEffects.OUTLINE) && this._outlinePass) {
            this._outlinePass = new OutlinePass(new THREE.Vector2(width, height), this.viewer.currentScene, this.viewer.currentCamera);
            this._outlinePass.edgeStrength = 7;
            this._outlinePass.hiddenEdgeColor = new THREE.Color(0x000000);
            this._effectComposer.addPass(this._outlinePass);
        }
        else if (this._outlinePass) {
            this._outlinePass.dispose();
            this._outlinePass = null;
        }

        if (this._currentPostEffects.hasFlag(PostEffects.PIXEL) && this._pixelPass) {
            // Pixelate
            this._pixelPass = new RenderPixelatedPass(16,this.viewer.currentScene,this.viewer.currentCamera);
            this._effectComposer.addPass(this._pixelPass);
        }

        if (this._currentPostEffects.hasFlag(PostEffects.GLITCH) && this._glitchPass) {
            this._effectComposer.addPass(this._glitchPass);
        }

        if (this._currentPostEffects.hasFlag(PostEffects.SAOS) && this._saoPass) {
            this._saoPass = new SAOPass(this.viewer.currentScene, this.viewer.currentCamera, false, true);
            this._saoPass.resolution.set(width, height);
            this._saoPass.params.saoBias = .01
            this._saoPass.params.saoIntensity = .0014
            this._saoPass.params.saoScale = .3
            this._saoPass.params.saoKernelRadius = 43
            this._saoPass.params.saoBlurRadius = 4
            this._saoPass.params.saoMinResolution = 0
        }

        this._copyPass = new ShaderPass(CopyShader);
        this._effectComposer.addPass(this._copyPass);
    }

    setOutline(objects: THREE.Object3D[]) {
        if (!objects) {
            console.error("PostProcessingManager::setOutline(): Passed object array can't be null!");
            return;
        }

        if (!this._currentPostEffects.hasFlag(PostEffects.OUTLINE) || !this._outlinePass) {
            if (window.location.host.includes('localhost'))
                console.error("PostProcessingManager::setOutline(): Outline effect was not activated. It is possible you forgot to active the outline effect.");
            return;
        }
        this._outlinePass.selectedObjects = objects;
    }

    clearOutline() {
        if (this._outlinePass)
            this._outlinePass.selectedObjects = [];
    }

    dispose() {
        this._outlinePass = null;
        this._saoPass = null;
        this._ssaaPass = null;
        this._fxaaPass = null;
        this._glitchPass = null;
        this._smaaPass = null;
        this._copyPass = null;
        this._pixelPass = null;
        this._renderPass = null;
        this._renderTarget?.dispose();
        this._renderTarget = null;
        this._effectComposer.passes = [];
        this._effectComposer;
    }

    handleResize(width, height) {
        if (!this.viewer._renderer)
            return;
        const pixelRatio = this.viewer._renderer.getPixelRatio();
        this._effectComposer.setSize(width, height);
        this._effectComposer.setPixelRatio(pixelRatio);
    }
}